import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Bell, RefreshCw, Landmark, Smartphone, Tv, Lightbulb, ArrowLeftRight, CreditCard, Fingerprint, Banknote, Receipt } from "lucide-react";
import { api, fmtDate } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useMeta } from "@/pages/Services";
import { Chip, Empty, rs } from "./ui";
import { Slip } from "./Slip";
import { ServiceTile } from "./Tile";
import { visibleServices } from "./services";

export function Brand({ user }) {
  const b = user.branding || {};
  const logo = b.logo_url && (b.logo_url.startsWith("/") ? `${process.env.REACT_APP_BACKEND_URL}${b.logo_url}` : b.logo_url);
  return (
    <div className="flex min-w-0 items-center gap-3" data-testid="m-brand">
      {logo ? <img src={logo} alt="" className="h-12 w-12 rounded-full border border-slate-200 bg-white object-contain p-1" data-testid="m-brand-logo" />
        : <div className="grid h-12 w-12 place-items-center rounded-full bg-[#F26B1D] font-display text-lg font-bold text-white">{(b.company_name || user.shop_name || user.name || "R")[0]}</div>}
      <div className="min-w-0">
        <div className="truncate text-xs text-slate-500" data-testid="m-brand-name">{b.company_name || user.shop_name || "Welcome"}</div>
        <div className="truncate font-display text-lg font-bold leading-tight text-[#0F2C47]">{user.name}</div>
      </div>
    </div>
  );
}

const SVC_ICON = { Payout: Landmark, Recharge: Smartphone, DTH: Tv, BBPS: Lightbulb, DMT: ArrowLeftRight, "Credit Card": CreditCard, AEPS: Fingerprint, "Cash Deposit": Banknote };

export function TxnRow({ t, onClick }) {
  const I = SVC_ICON[t.service] || SVC_ICON[Object.keys(SVC_ICON).find((k) => t.service?.startsWith(k))] || Receipt;
  const credit = ["AEPS Cash Withdrawal", "Aadhaar Pay"].includes(t.service);
  return (
    <button onClick={() => onClick(t)} className="flex w-full items-center gap-3 border-b border-slate-100 py-3 text-left last:border-0 active:bg-slate-50" data-testid={`m-txn-${t.txn_id}`}>
      <span className="grid h-10 w-10 shrink-0 place-items-center rounded-xl bg-slate-100 text-[#0F2C47]"><I className="h-5 w-5" /></span>
      <div className="min-w-0 flex-1">
        <div className="truncate text-sm font-semibold">{t.service === "Payout" ? "Move to Bank" : t.service}</div>
        <div className="truncate text-xs text-slate-500">{t.service === "Payout" ? `${t.details?.holder_name || ""} · ${t.number}` : t.number || t.operator}</div>
      </div>
      <div className="text-right">
        <div className={`text-sm font-bold ${t.status === "FAILED" || t.status === "REFUNDED" ? "text-slate-400 line-through" : credit ? "text-emerald-600" : "text-red-600"}`}>{credit ? "+" : "-"} {rs(t.amount)}</div>
        <div className="flex items-center justify-end gap-1 text-[10px] text-slate-500">{t.status !== "SUCCESS" && <Chip s={t.status} />}{fmtDate(t.created_at).slice(0, 17)}</div>
      </div>
    </button>
  );
}

export function MHome() {
  const { user, wallet, refreshBalance } = useAuth();
  const meta = useMeta();
  const [recent, setRecent] = useState([]);
  const [unread, setUnread] = useState(0);
  const [slip, setSlip] = useState(null);
  const [spin, setSpin] = useState(false);
  const load = async () => {
    setSpin(true);
    await Promise.all([refreshBalance(), api.get("/transactions").then(({ data }) => setRecent(data.slice(0, 5))),
      api.get("/notifications").then(({ data }) => setUnread(data.unread)).catch(() => {})]);
    setSpin(false);
  };
  useEffect(() => { load(); }, []); // eslint-disable-line
  const services = meta ? visibleServices(user, meta) : [];
  return (
    <div className="bg-white px-4 pb-28 pt-[max(1rem,env(safe-area-inset-top))]" data-testid="m-home">
      <div className="flex items-center justify-between">
        <Brand user={user} />
        <Link to="/app/notifications" className="relative grid h-11 w-11 place-items-center rounded-full text-[#0F2C47]" data-testid="m-notif-bell">
          <Bell className="h-6 w-6" />{unread > 0 && <span className="absolute right-1.5 top-1.5 grid h-4 min-w-4 place-items-center rounded-full bg-[#F26B1D] px-1 text-[10px] font-bold text-white" data-testid="m-notif-count">{unread}</span>}
        </Link>
      </div>
      <div className="relative mt-4 overflow-hidden rounded-2xl bg-[#0F2C47] p-5 text-white" data-testid="m-wallet-card">
        <div className="pointer-events-none absolute -right-10 -top-12 h-40 w-40 rounded-full border-[22px] border-white/[0.05]" />
        <div className="flex items-start justify-between">
          <div><div className="text-sm text-white/70">Wallet Balance</div><div className="mt-1 font-display text-3xl font-bold" data-testid="m-main-balance">{rs(wallet.balance)}</div></div>
          <button onClick={load} className="grid h-9 w-9 place-items-center rounded-full bg-white/10" data-testid="m-refresh"><RefreshCw className={`h-4 w-4 ${spin ? "animate-spin" : ""}`} /></button>
        </div>
        <div className="mt-4 flex items-center justify-between border-t border-white/10 pt-3 text-sm" data-testid="m-aeps-balance">
          <span className="flex items-center gap-2 text-white/80"><span className="h-3 w-4 rounded-sm bg-[#F26B1D]" />AEPS Wallet</span><b className="font-display text-base">{rs(wallet.aeps_balance)}</b>
        </div>
      </div>
      <div className="mt-5 grid grid-cols-4 gap-x-3 gap-y-4" data-testid="m-quick-actions">
        {services.slice(0, 8).map((x) => <ServiceTile key={x.key} s={x} />)}
      </div>
      <div className="mb-1 mt-6 flex items-center justify-between"><h2 className="font-display text-base font-bold">Latest Transactions</h2><Link to="/app/reports" className="text-sm font-semibold text-[#F26B1D]" data-testid="m-view-all">View All</Link></div>
      <div data-testid="m-recent">
        {recent.length ? recent.map((t) => <TxnRow key={t.txn_id} t={t} onClick={setSlip} />) : <Empty text="No transactions yet" />}
      </div>
      {slip && <Slip txn={slip} onClose={() => setSlip(null)} />}
    </div>
  );
}
